
import React, { useEffect, useMemo, useRef, useState } from "react";

/*
 * Редактор поверх картинки вывески.
 *
 *   — рамки блоков: навели — подсветка, щёлкнули — блок выбран, слева открываются его поля;
 *   — граница между блоками тянется мышкой: один блок выше, соседний ниже;
 *   — шапка: у выбранной шапки логотип и надпись (название + подпись) тянутся мышкой,
 *     колесо над ними — больше / меньше; сама шапка от этого не растёт;
 *   — фото в блоке преимуществ: выбранный блок тянется мышкой (сдвиг), колесо — увеличение;
 *   — кнопки выбранного блока (выше, ниже, скрыть, сброс) — над картинкой, в панели просмотра.
 *
 * Картинку рисует сервер. Пока тянем — двигаются рамки, отпустили — сервер перерисовывает.
 * Координаты блоков приходят с сервера в мм (layout.boxes) и переводятся в проценты картинки.
 */

export const BLOCK_NAMES = {
    header: "Шапка",
    features: "Преимущества",
    services: "Наши услуги",
    contacts: "Контакты",
};

export const PART_NAMES = {
    logo: "Логотип",
    name: "Название",
    tagline: "Подпись",
};

/** Какие поля шапки у куска: сдвиг и размер. Название ведёт за собой подпись. */
export const PART_FIELDS = {
    logo: { x: "logoX", y: "logoY", s: "logoScale" },
    name: { x: "textX", y: "textY", s: "nameScale" },
    tagline: { x: "taglineX", y: "taglineY", s: "taglineScale" },
};

export const HEADER_DEFAULTS = {
    logoScale: 1, nameScale: 1, taglineScale: 1, logoX: 0, logoY: 0, textX: 0, textY: 0, taglineX: 0, taglineY: 0,
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const round = (value, digits = 3) => Math.round(value * 10 ** digits) / 10 ** digits;
const DRAG_THRESHOLD = 4;

const SignCanvas = ({
    url,
    layout,
    selected,
    onSelect,
    onResize,
    imageSettings,
    hasImage,
    onImageChange,
    header,
    onHeaderChange,
    disabled,
}) => {
    const overlayRef = useRef(null);
    const [drag, setDrag] = useState(null);
    const [preview, setPreview] = useState(null);
    const [hovered, setHovered] = useState("");
    const headerValue = { ...HEADER_DEFAULTS, ...(header || {}) };

    // Пришла новая раскладка с сервера — временные рамки больше не нужны.
    useEffect(() => {
        setPreview(null);
    }, [layout]);

    const boxes = useMemo(() => {
        const list = (layout?.boxes || []).map((box) => ({ ...box }));
        if (preview?.kind === "resize") {
            const a = list[preview.index];
            const b = list[preview.index + 1];
            if (a && b) {
                a.h = preview.ha;
                b.y = a.y + a.h + (layout.gapMm || 0);
                b.h = preview.hb;
            }
        }
        return list;
    }, [layout, preview]);

    const pct = (value, total) => `${(value / total) * 100}%`;
    const toPercent = (box) => ({
        left: pct(box.x, layout.widthMm),
        top: pct(box.y, layout.heightMm),
        width: pct(box.w, layout.widthMm),
        height: pct(box.h, layout.heightMm),
    });

    const pxPerMm = () => {
        const rect = overlayRef.current?.getBoundingClientRect();
        return rect && layout?.heightMm ? rect.height / layout.heightMm : 1;
    };

    // Колесо: над фото преимуществ — увеличение фото, над логотипом или надписью шапки — их размер.
    // Обычный слушатель: в React колесо «пассивное» и прокрутку страницы не отменить.
    const wheelRef = useRef({ timer: 0, pending: null });
    wheelRef.current = { ...wheelRef.current, imageSettings, onImageChange, hasImage, selected, headerValue, onHeaderChange };
    useEffect(() => {
        const element = overlayRef.current;
        if (!element) return undefined;
        const onWheel = (event) => {
            const state = wheelRef.current;
            const step = event.deltaY < 0 ? 0.05 : -0.05;
            const partNode = event.target.closest?.("[data-part]");
            if (state.selected === "header" && partNode && state.onHeaderChange) {
                event.preventDefault();
                const part = partNode.getAttribute("data-part");
                const field = (PART_FIELDS[part] || PART_FIELDS.name).s;
                const base = state.pending || state.headerValue;
                const next = { [field]: round(clamp(base[field] + step, 0.4, 2.5), 2) };
                state.pending = { ...base, ...next };
                setPreview({ kind: "zoomPart", part, scale: next[field] });
                window.clearTimeout(state.timer);
                state.timer = window.setTimeout(() => {
                    const pending = state.pending;
                    state.pending = null;
                    state.onHeaderChange({
                        logoScale: pending.logoScale, nameScale: pending.nameScale, taglineScale: pending.taglineScale,
                    });
                }, 250);
                return;
            }
            if (state.selected !== "features" || !state.hasImage || !event.target.closest?.(".is-features")) return;
            event.preventDefault();
            const current = state.pending?.scale ?? state.imageSettings.scale;
            const next = round(clamp(current + step, 1, 4), 2);
            state.pending = { scale: next };
            setPreview({ kind: "zoom", scale: next });
            window.clearTimeout(state.timer);
            state.timer = window.setTimeout(() => {
                state.onImageChange({ scale: next });
                state.pending = null;
            }, 250);
        };
        element.addEventListener("wheel", onWheel, { passive: false });
        return () => element.removeEventListener("wheel", onWheel);
    }, [url]);

    if (!url || !layout?.boxes?.length) return null;

    const headerBox = boxes.find((box) => box.type === "header");
    const showParts = selected === "header" && headerBox?.parts?.length && onHeaderChange;

    /* ---------------- Тянем границу между блоками ---------------- */

    const startResize = (event, index) => {
        if (disabled) return;
        event.preventDefault();
        event.stopPropagation();
        const a = boxes[index];
        const b = boxes[index + 1];
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setDrag({ kind: "resize", index, startY: event.clientY, ha: a.h, hb: b.h, needA: a.need, needB: b.need });
    };

    /* ---------------- Тянем логотип или надпись шапки ---------------- */

    const startPart = (event, part) => {
        if (disabled || event.button > 0) return;
        event.preventDefault();
        event.stopPropagation();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setDrag({
            kind: "part",
            part: part.key,
            startX: event.clientX,
            startY: event.clientY,
            baseX: headerValue[(PART_FIELDS[part.key] || PART_FIELDS.name).x],
            baseY: headerValue[(PART_FIELDS[part.key] || PART_FIELDS.name).y],
            moved: false,
        });
    };

    /* ---------------- Тянем фото / щёлкаем по блоку ---------------- */

    const startBox = (event, box) => {
        if (disabled || event.button > 0) return;
        const canPan = box.type === "features" && selected === "features" && hasImage;
        const rect = event.currentTarget.getBoundingClientRect();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        setDrag({
            kind: canPan ? "pan" : "click",
            type: box.type,
            startX: event.clientX,
            startY: event.clientY,
            width: rect.width,
            height: rect.height,
            offsetX: imageSettings?.offsetX || 0,
            offsetY: imageSettings?.offsetY || 0,
            moved: false,
        });
    };

    const onPointerMove = (event) => {
        if (!drag) return;
        if (drag.kind === "resize") {
            const delta = (event.clientY - drag.startY) / pxPerMm();
            const total = drag.ha + drag.hb;
            const ha = clamp(drag.ha + delta, drag.needA, total - drag.needB);
            setPreview({ kind: "resize", index: drag.index, ha, hb: total - ha });
            return;
        }
        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;
        const moved = drag.moved || Math.hypot(dx, dy) > DRAG_THRESHOLD;
        if (moved !== drag.moved) setDrag({ ...drag, moved });
        if (drag.kind === "part" && moved) {
            const k = pxPerMm();
            setPreview({ kind: "part", part: drag.part, dxMm: dx / k, dyMm: dy / k });
            return;
        }
        if (drag.kind === "pan" && moved) {
            // Тянем картинку вправо — видно её левую часть, то есть сдвиг уменьшается.
            setPreview({
                kind: "pan",
                offsetX: clamp(drag.offsetX - (dx / drag.width) * 2, -1, 1),
                offsetY: clamp(drag.offsetY - (dy / drag.height) * 2, -1, 1),
            });
        }
    };

    const onPointerUp = () => {
        if (!drag) return;
        if (drag.kind === "resize" && preview?.kind === "resize") {
            // Свои доли свободного места: каждому блоку — сколько у него сверх нужного содержимому.
            const weights = {};
            boxes.forEach((box) => {
                weights[box.type] = Math.max(0, Math.round((box.h - box.need) * 100) / 100);
            });
            onResize(weights);
        } else if (drag.kind === "part" && preview?.kind === "part" && headerBox) {
            const fields = PART_FIELDS[drag.part] || PART_FIELDS.name;
            onHeaderChange({
                [fields.x]: round(clamp(drag.baseX + preview.dxMm / headerBox.w, -1, 1)),
                [fields.y]: round(clamp(drag.baseY + preview.dyMm / headerBox.h, -1, 1)),
            });
        } else if (drag.kind === "pan" && preview?.kind === "pan") {
            onImageChange({ offsetX: round(preview.offsetX, 2), offsetY: round(preview.offsetY, 2) });
        } else if (!drag.moved && drag.type) {
            onSelect(drag.type);
        }
        setDrag(null);
        if (preview?.kind === "pan") setPreview(null);
    };

    const panInfo = preview?.kind === "pan"
        ? `Сдвиг: ${Math.round(preview.offsetX * 100)}% · ${Math.round(preview.offsetY * 100)}%`
        : preview?.kind === "zoom" ? `Увеличение ×${preview.scale.toFixed(2)}`
            : preview?.kind === "zoomPart" ? `${PART_NAMES[preview.part]}: ${Math.round(preview.scale * 100)}%` : "";

    return (
        <div
            ref={overlayRef}
            className={`printSigns__overlay${drag ? " is-dragging" : ""}`}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onPointerDown={(event) => {
                if (event.target === overlayRef.current) onSelect("");
            }}
        >
            {boxes.map((box) => {
                const isSelected = box.type === selected;
                const canPan = box.type === "features" && isSelected && hasImage;
                return (
                    <div
                        key={box.type}
                        className={`printSigns__box is-${box.type}${isSelected ? " is-selected" : ""}${hovered === box.type ? " is-hovered" : ""}${canPan ? " can-pan" : ""}`}
                        style={toPercent(box)}
                        onPointerDown={(event) => startBox(event, box)}
                        onPointerEnter={() => setHovered(box.type)}
                        onPointerLeave={() => setHovered("")}
                        title={canPan ? "Тяните — сдвиг фото, колесо — увеличение" : `${BLOCK_NAMES[box.type]}: щёлкните, чтобы править`}
                    >
                        {!isSelected ? <span className="printSigns__boxLabel">{BLOCK_NAMES[box.type]}</span> : null}
                    </div>
                );
            })}

            {showParts ? headerBox.parts.map((part) => {
                const shift = preview?.kind === "part" && preview.part === part.key ? preview : null;
                const box = { ...part, x: part.x + (shift?.dxMm || 0), y: part.y + (shift?.dyMm || 0) };
                return (
                    <div
                        key={part.key}
                        data-part={part.key}
                        className={`printSigns__part is-${part.key}${shift ? " is-moving" : ""}`}
                        style={toPercent(box)}
                        onPointerDown={(event) => startPart(event, part)}
                        title={`${PART_NAMES[part.key]}: тяните — сдвиг, колесо — больше / меньше`}
                    >
                        <span className="printSigns__partLabel">{PART_NAMES[part.key] || part.key}</span>
                    </div>
                );
            }) : null}

            {boxes.map((box, index) => {
                const next = boxes[index + 1];
                if (!next || next.column !== box.column) return null;
                const room = box.h + next.h - box.need - next.need;
                const y = box.y + box.h + (layout.gapMm || 0) / 2;
                return (
                    <div
                        key={`handle-${box.type}`}
                        className={`printSigns__handle${room < 1 ? " is-locked" : ""}${drag?.kind === "resize" && drag.index === index ? " is-active" : ""}`}
                        style={{
                            left: pct(box.x, layout.widthMm),
                            width: pct(box.w, layout.widthMm),
                            top: pct(y, layout.heightMm),
                        }}
                        title={room < 1 ? "Свободного места нет: всё занято содержимым. Уменьшите размер текста или уберите пункты." : "Тяните — поменять высоту блоков"}
                        onPointerDown={(event) => room >= 1 && startResize(event, index)}
                    >
                        <i />
                    </div>
                );
            })}

            {panInfo ? <div className="printSigns__panInfo">{panInfo}</div> : null}
        </div>
    );
};

export default SignCanvas;
